import { useEffect, useLayoutEffect, useRef, useState } from "react";
type Dir = -1 | 1;


/** * Custom hook to manage horizontal scrolling of an element.
*
* - Returns a `ref` that should be attached to the scrollable element.
* - Tracks current scroll position (`scrollLeft`) and maximum scrollable distance (`maxScroll`).
* - Provides controls to programmatically start and stop horizontal scrolling.
* - Recalculates max scroll when the dependencies (passed via `args`) change.
*
* @param {...any} args - Optional dependencies (typically content-related) used to re-trigger scroll calculations.
*
* @returns {{
*   scrollBlockRef: React.RefObject<HTMLDivElement>;  // Ref to be attached to the scrollable element.
*   scrollLeft: number;                               // Current horizontal scroll position.
*   maxScroll: number;                                // Maximum scrollable distance.
*   startScroll: (offset: number) => void;            // Starts auto-scrolling by the given offset per tick.
*   stopScroll: () => void;                           // Stops auto-scrolling.
* }}
*/
export const useHorizontalScroll = (...args: any) => {
    const scrollBlockRef = useRef<HTMLDivElement>(null);

    // Timers
    const delayRef = useRef<number | null>(null);
    const intervalRef = useRef<number | null>(null);
    // Supress click if holding
    const didRepeatRef = useRef(false);

    const scSetPending = useRef(false);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [maxScroll, setMaxScroll] = useState(0);
    const [itemWidth, setItemWidth] = useState(0);

    // On load setup
    useEffect(() => {
        const el = scrollBlockRef.current;
        if (!el) return;
        // Default scroll func
        const onScroll = () => {
            if (scSetPending.current) return;
            scSetPending.current = true;
            requestAnimationFrame(() => {
                setScrollLeft(el.scrollLeft);
                scSetPending.current = false;
            });
        };
        el.addEventListener("scroll", onScroll, { passive: true });
        return () => el.removeEventListener("scroll", onScroll);
    }, []);
    // Scroll change - reload on arguments change
    useLayoutEffect(() => {
        const el = scrollBlockRef.current;
        if (!el) return;
        // Get the container size 
        const measure = () => {
            setMaxScroll(el.scrollWidth - el.clientWidth);

            const first = el.firstElementChild as HTMLElement | null;
            if (first) {
                const styles = getComputedStyle(el);
                const gap =
                parseFloat(styles.columnGap || "0") ||
                parseFloat(styles.gap || "0") ||
                0;
                setItemWidth(first.offsetWidth + gap);
            }
        };
        measure();
        const id = requestAnimationFrame(measure);
        return () => cancelAnimationFrame(id);
    }, [...args]);

    // Scroll by one product on click
    const scrollOne = (dir: Dir) => {
        const el = scrollBlockRef.current;
        if (!el || !itemWidth) return;
        el.scrollBy({ left: dir * itemWidth, behavior: "smooth" });
    };

    // One step - used in hold
    const stepBy = (dir: Dir, stepPx: number) => {
        const el = scrollBlockRef.current;
        if (!el) return;
        // Remaining scroll field
        const remaining = dir < 0 ? el.scrollLeft : (maxScroll - el.scrollLeft);
        // Scoll amount required
        const amount = Math.min(stepPx, Math.max(0, remaining));
        // Stop on max scroll
        if (amount <= 0) {
            stopHold();
            return;
        }
        el.scrollBy({ left: dir * amount, behavior: "smooth" });
    };

    // Hold listener
    // Change periodMS  to change speed
    const startHold = (
        dir: Dir,
        { delayMs = 60, stepPx = 200, periodMs = 500 } = {}
    ) => {
        // Already running
        if (delayRef.current || intervalRef.current) return;
        didRepeatRef.current = false;
        // Run steps in delay
        delayRef.current = window.setTimeout(() => {
            delayRef.current = null;
            didRepeatRef.current = true;            // mark that it became a hold
            stepBy(dir, stepPx);                    // do first step immediately
            intervalRef.current = window.setInterval(() => {
                stepBy(dir, stepPx);                  // repeat step each period
            }, periodMs);
        }, delayMs);
    };
    // Stop
    const stopHold = () => {
        if (delayRef.current) { clearTimeout(delayRef.current); delayRef.current = null; }
        if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    };

    // helper so buttons can know whether to suppress click after a hold
    const didAutoRepeat = () => didRepeatRef.current;

    useEffect(() => stopHold, []);

    return { scrollBlockRef, scrollLeft, maxScroll, scrollOne, startHold, stopHold, didAutoRepeat };
};
